/**
 * Profile Assistant Context
 * 
 * Provides state management and API integration for the Profile Assistant.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { PROFILE_SECTIONS, COMMON_PROFILE_QUESTIONS, PROFILE_WORKFLOWS } from './profileKnowledge';

const generateSessionId = () => {
  return `profile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const ProfileAssistantContext = createContext(null);

export const ProfileAssistantProvider = ({ children }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  
  // Panel state
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  
  // Session state
  const [sessionId, setSessionId] = useState(() => generateSessionId());
  
  // Context tracking
  const [currentSection, setCurrentSection] = useState('overview');
  
  // Conversation state
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Refs
  const pendingSave = useRef(false);
  const saveTimeout = useRef(null);

  // Toggle panel
  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  // Close panel
  const closePanel = useCallback(() => {
    if (!isPinned) {
      setIsPanelOpen(false);
    }
  }, [isPinned]);

  // Toggle pin
  const togglePin = useCallback(() => {
    setIsPinned(prev => !prev);
  }, []);

  // Unpin and close
  const unpinAndClose = useCallback(() => {
    setIsPinned(false);
    setIsPanelOpen(false);
  }, []);

  // Update current section being viewed
  const updateCurrentSection = useCallback((section) => {
    setCurrentSection(section);
  }, []);

  // Add message to conversation
  const addMessage = useCallback((message) => {
    setConversationHistory(prev => [...prev, {
      ...message,
      timestamp: new Date().toISOString()
    }]);
  }, []);

  // Clear conversation
  const clearConversation = useCallback(() => {
    setConversationHistory([]);
    setSessionId(generateSessionId());
  }, []);

  // Get full context for AI
  const getFullContext = useCallback(() => {
    return {
      currentSection,
      sectionInfo: PROFILE_SECTIONS[currentSection] || PROFILE_SECTIONS.overview,
      allSections: Object.keys(PROFILE_SECTIONS).map(key => ({
        key,
        name: PROFILE_SECTIONS[key].name,
        description: PROFILE_SECTIONS[key].description
      })),
      commonQuestions: COMMON_PROFILE_QUESTIONS,
      workflows: PROFILE_WORKFLOWS,
      conversationLength: conversationHistory.length,
      sessionId
    };
  }, [currentSection, conversationHistory.length, sessionId]);

  // Send message to AI
  const handleSendMessage = useCallback(async (messageText) => {
    if (!messageText?.trim() || isLoading) return;

    // Add user message
    addMessage({
      role: 'user',
      content: messageText.trim()
    });

    setIsLoading(true);

    try {
      const fullContext = getFullContext();
      
      const response = await authenticatedAxios.post('/profile/assistant', {
        message: messageText.trim(),
        context: fullContext,
        conversationHistory: conversationHistory.slice(-10)
      });

      addMessage({
        role: 'assistant',
        content: response.data.message || "I'm sorry, I couldn't process that request."
      });
    } catch (error) {
      console.error('[ProfileAssistant] Error sending message:', error);
      addMessage({
        role: 'assistant',
        content: "I apologize, but I encountered an error. Please try again or refresh the page."
      });
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedAxios, conversationHistory, getFullContext, isLoading, addMessage]);

  // Save conversation (debounced)
  const saveConversation = useCallback(async () => {
    if (pendingSave.current || conversationHistory.length === 0) return;
    
    pendingSave.current = true;
    
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    
    saveTimeout.current = setTimeout(async () => {
      try {
        await authenticatedAxios.post('/profile/assistant/conversation', {
          sessionId,
          messages: conversationHistory
        });
      } catch (error) {
        console.error('[ProfileAssistant] Error saving conversation:', error);
      } finally {
        pendingSave.current = false;
      }
    }, 2000);
  }, [authenticatedAxios, sessionId, conversationHistory]);

  const value = {
    // Panel state
    isPanelOpen,
    setIsPanelOpen,
    togglePanel,
    closePanel,
    isPinned,
    togglePin,
    unpinAndClose,
    
    // Session
    sessionId,
    
    // Context
    currentSection,
    updateCurrentSection,
    
    // Conversation
    conversationHistory,
    addMessage,
    clearConversation,
    isLoading,
    setIsLoading,
    
    // Actions
    handleSendMessage,
    getFullContext,
    saveConversation
  };

  return (
    <ProfileAssistantContext.Provider value={value}>
      {children}
    </ProfileAssistantContext.Provider>
  );
};

export const useProfileAssistant = () => {
  const context = useContext(ProfileAssistantContext);
  if (!context) {
    throw new Error('useProfileAssistant must be used within a ProfileAssistantProvider');
  }
  return context;
};

export default ProfileAssistantContext;

